import { createSelector } from '@reduxjs/toolkit';
import type { ICourtSchedule, IDaySchedule } from '../../types/schedule';
import { isFreeSlot } from '../../types/schedule';
import { pureFilterScheduleBy } from '../../pages/tourDetails/Schedule/components/schedule.helpers';
import { selectScheduleData, selectScheduleCurrentFilters } from './scheduleSelectors';
import { MAX_SCHEDULE_BULK_SELECTION } from './scheduleSelectionConstants';

export type ScheduleGridDayInput = {
  day: IDaySchedule;
  /** Changes only when any courtSchedule ref or scale-relevant bounds change */
  courtRefsKey: string;
};

const courtScheduleRefIds = new WeakMap<ICourtSchedule, number>();
let nextCourtScheduleRefId = 0;

function courtScheduleRefToken(courtSchedule: ICourtSchedule): number {
  let id = courtScheduleRefIds.get(courtSchedule);
  if (id === undefined) {
    id = ++nextCourtScheduleRefId;
    courtScheduleRefIds.set(courtSchedule, id);
  }
  return id;
}

export function buildCourtRefsKey(day: IDaySchedule): string {
  return day.courts
    .map((c) => `${c.court.id}:${courtScheduleRefToken(c)}`)
    .join('|');
}

export const selectScheduleGridDayInput = createSelector(
  [
    selectScheduleData,
    selectScheduleCurrentFilters,
    (_state: unknown, selectedDayKey: number) => selectedDayKey,
  ],
  (schedule, filters, selectedDayKey): ScheduleGridDayInput | null => {
    if (!schedule) return null;
    const filtered =
      filters.length === 0 ? schedule : pureFilterScheduleBy(schedule, filters);
    const day = filtered.days.find((d) => d.dayKey === selectedDayKey);
    if (!day) return null;
    return { day, courtRefsKey: buildCourtRefsKey(day) };
  }
);

/** Match IDs visible on the current day's grid (respects filters). Used for Ctrl+A select-all. */
export const selectBulkSelectableMatchIdsForDay = createSelector(
  [selectScheduleGridDayInput],
  (gridInput): number[] => {
    if (!gridInput) return [];
    const ids: number[] = [];
    const seen = new Set<number>();
    for (const courtSchedule of gridInput.day.courts) {
      for (const item of courtSchedule.matches) {
        if (isFreeSlot(item)) continue;
        if (seen.has(item.id)) continue;
        seen.add(item.id);
        ids.push(item.id);
        if (ids.length >= MAX_SCHEDULE_BULK_SELECTION) return ids;
      }
    }
    return ids;
  }
);
